<template>
  <v-container id="campaigns-view" fluid tag="section" class="dashboard-container" style="padding: 24px 96px;">
    <v-row class="mb-2 ma-n2" align="center">
      <v-col cols="12" sm="auto" class="pa-2">
        <div class="d-flex align-center">
          <v-icon color="#00E5FF" large class="mr-3">mdi-bullhorn</v-icon>
          <h1 class="text-h5 font-weight-bold text--primary">{{ $t('campaigns.title') }}</h1>
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="12" sm="auto" class="pa-2">
        <v-btn
          v-if="canEdit && !isViewOnlyMode"
          color="#00E5FF"
          height="40"
          depressed
          class="px-5 font-weight-bold action-btn black--text"
          style="border-radius: 8px;"
          @click="openCreateDialog"
        >
          <v-icon left color="black">mdi-plus</v-icon> {{ $t('campaigns.new') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-2 ma-n2 mt-2 align-center">
      <v-col cols="12" md="4" class="pa-2">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('campaigns.searchByName')"
          outlined
          dense
          hide-details
          clearable
          class="elevment-input"
          color="#00E5FF"
          @input="debouncedSearch"
        />
      </v-col>
      <v-col cols="12" md="2" class="pa-2">
        <v-select
          v-model="filterStatus"
          :items="statusOptionsFiltered"
          placeholder="Status"
          outlined
          dense
          clearable
          hide-details
          class="elevment-input"
          color="#00E5FF"
        />
      </v-col>
      <v-col cols="12" md="2" class="pa-2">
        <v-menu v-model="filterStartDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filterDate"
              :placeholder="$t('campaigns.startDate')"
              prepend-inner-icon="mdi-calendar"
              readonly
              clearable
              outlined
              dense
              hide-details
              class="elevment-input"
              color="#00E5FF"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="filterDate" color="#00E5FF" @input="filterStartDateMenu = false" />
        </v-menu>
      </v-col>
      <v-col cols="12" md="2" class="pa-2">
        <v-menu v-model="filterEndDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filterEndDate"
              :placeholder="$t('campaigns.endDate')"
              prepend-inner-icon="mdi-calendar"
              readonly
              clearable
              outlined
              dense
              hide-details
              class="elevment-input"
              color="#00E5FF"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="filterEndDate" color="#00E5FF" @input="filterEndDateMenu = false" />
        </v-menu>
      </v-col>
      <v-col cols="12" md="2" class="pa-2 d-flex align-center">
        <v-btn text color="grey" class="font-weight-bold px-3" style="border-radius: 8px; letter-spacing: 0;" @click="clearFilters">
          <v-icon left small>mdi-filter-off</v-icon> Limpar
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="ma-n2 mt-2">
      <v-col cols="12" class="pa-2">
        <v-card class="elevment-card elevation-0">
          <v-data-table
            :headers="headers"
            :items="filteredCampaigns"
            :loading="loading"
            hide-default-footer
            disable-pagination
            class="transparent-bg custom-table"
          >
            <template v-slot:item.name="{ item }">
              <a class="campaign-name-link font-weight-bold" @click="viewCampaign(item)">
                {{ item.name }}
              </a>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip outlined x-small class="theme-badge elevation-0" :class="getStatusChipClass(item.status)">
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.startDate="{ item }">
              <span class="text-body-2 text--secondary">{{ formatDate(item.startDate) }}</span>
            </template>

            <template v-slot:item.endDate="{ item }">
              <span class="text-body-2 text--secondary">{{ formatDate(item.endDate) }}</span>
            </template>

            <template v-slot:item.target="{ item }">
              <div class="d-flex align-center text-body-2 text--secondary">
                <v-icon x-small color="grey" class="mr-2">mdi-account-group</v-icon>
                {{ getTargetSummary(item) }}
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-end" style="gap: 4px">
                
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small :disabled="!item.emailEnabled || isViewOnlyMode || !canEdit" v-bind="attrs" v-on="on" @click="sendEmail(item)">
                      <v-icon size="18" :color="item.emailEnabled ? '#1976D2' : 'grey'">mdi-email-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ item.emailEnabled ? 'Disparar E-mail' : 'E-mail desabilitado' }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small :disabled="!item.teamsEnabled || isViewOnlyMode || !canEdit" v-bind="attrs" v-on="on" @click="sendTeams(item)">
                      <v-icon size="18" :color="item.teamsEnabled ? '#7B1FA2' : 'grey'">mdi-microsoft-teams</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ item.teamsEnabled ? 'Disparar Teams' : 'Sem integração Teams' }}</span>
                </v-tooltip>

                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on">
                      <v-icon size="20" class="text--secondary">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="elevment-dialog-card py-1">
                    <v-list-item @click="viewCampaign(item)">
                      <v-list-item-icon class="mr-2 my-1"><v-icon small>mdi-eye</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-body-2 text--primary">Visualizar</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canEdit" :disabled="isViewOnlyMode || item.status === 'COMPLETED'" @click="editCampaign(item)">
                      <v-list-item-icon class="mr-2 my-1"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-body-2 text--primary">Editar</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canEdit" :disabled="isViewOnlyMode" @click="duplicateCampaign(item)">
                      <v-list-item-icon class="mr-2 my-1"><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-body-2 text--primary">Duplicar</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canEdit" :disabled="isViewOnlyMode || item.status !== 'DRAFT'" @click="launchCampaign(item)">
                      <v-list-item-icon class="mr-2 my-1"><v-icon small color="success">mdi-rocket-launch</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-body-2 text--primary">Ativar</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="viewStats(item)">
                      <v-list-item-icon class="mr-2 my-1"><v-icon small color="#00E5FF">mdi-chart-line</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-body-2 text--primary">Estatísticas</v-list-item-title>
                    </v-list-item>
                    <v-divider class="theme-divider my-1" />
                    <v-list-item v-if="canEdit" :disabled="isViewOnlyMode" @click="deleteCampaign(item)">
                      <v-list-item-icon class="mr-2 my-1"><v-icon small color="error">mdi-delete</v-icon></v-list-item-icon>
                      <v-list-item-title class="text-body-2 error--text font-weight-bold">Excluir</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </template>
          </v-data-table>

          <div v-if="loading" class="pa-10 text-center">
            <v-progress-circular indeterminate color="#00E5FF" size="48" />
            <p class="mt-4 text--secondary">Carregando campanhas...</p>
          </div>
          <div v-if="!loading && filteredCampaigns.length === 0" class="pa-10 text-center">
            <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-bullhorn-outline</v-icon>
            <h3 class="text-h6 text--primary mt-5">Nenhuma campanha encontrada</h3>
            <p class="text-body-2 text--secondary mb-0">Tente ajustar seus filtros ou crie uma nova campanha.</p>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="800px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4">
          <h3 class="text-h5 font-weight-bold text--primary">{{ editMode ? 'Editar Campanha' : 'Nova Campanha' }}</h3>
        </v-card-title>
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6 custom-scroll" style="max-height: 70vh; overflow-y: auto;">
          <v-form ref="form" v-model="validForm">
            
            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Nome da Campanha *</div>
            <v-text-field v-model="editedItem.name" placeholder="Ex: Avaliação Q1 2026" :rules="[rules.required]" outlined dense class="elevment-input mb-4" color="#00E5FF" hide-details="auto" />

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Descrição</div>
            <v-textarea v-model="editedItem.description" placeholder="Descreva os objetivos e foco desta campanha" outlined dense rows="2" class="elevment-input mb-4" color="#00E5FF" hide-details="auto" />

            <v-row dense class="mb-4">
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Data de Início *</div>
                <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="editedItem.startDate" placeholder="Selecione..." :rules="[rules.required]" prepend-inner-icon="mdi-calendar" readonly outlined dense class="elevment-input" color="#00E5FF" hide-details="auto" v-bind="attrs" v-on="on" />
                  </template>
                  <v-date-picker v-model="editedItem.startDate" :min="today" :max="editedItem.endDate" color="#00E5FF" @input="startDateMenu = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Data de Término *</div>
                <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="editedItem.endDate" placeholder="Selecione..." :rules="[rules.required]" prepend-inner-icon="mdi-calendar" readonly outlined dense class="elevment-input" color="#00E5FF" hide-details="auto" v-bind="attrs" v-on="on" />
                  </template>
                  <v-date-picker v-model="editedItem.endDate" :min="editedItem.startDate || today" color="#00E5FF" @input="endDateMenu = false" />
                </v-menu>
              </v-col>
            </v-row>

            <v-divider class="theme-divider my-4" />

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Status *</div>
            <v-select v-model="editedItem.status" :items="statusOptions" :rules="[rules.required]" outlined dense class="elevment-input mb-4" color="#00E5FF" hide-details="auto" />

            <template v-if="editedItem.status === 'CANCELLED'">
              <div class="text-subtitle-2 font-weight-bold error--text mb-2">Motivo do Cancelamento *</div>
              <v-select v-model="editedItem.cancellationReason" :items="cancellationReasons" :rules="[rules.required]" outlined dense clearable class="elevment-input mb-4" color="error" hide-details="auto" />
              
              <div v-if="editedItem.cancellationReason" class="text-subtitle-2 font-weight-bold text--primary mb-2">Observações Adicionais</div>
              <v-textarea v-if="editedItem.cancellationReason" v-model="editedItem.cancellationNotes" outlined dense rows="2" class="elevment-input mb-4" color="error" hide-details="auto" />
            </template>

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Fonte de Disparo Notificações *</div>
            <div class="d-flex flex-wrap mb-4" style="gap: 12px">
              <v-card outlined rounded="lg" class="flex-grow-1 text-center pa-3 source-card" :class="{ 'source-card--active': editedItem.notificationSource === 'email' }" @click="editedItem.notificationSource = 'email'">
                <v-icon size="24" :color="editedItem.notificationSource === 'email' ? '#00E5FF' : 'grey'">mdi-email-outline</v-icon>
                <div class="text-caption font-weight-bold mt-1" :class="editedItem.notificationSource === 'email' ? 'text--primary' : 'text--secondary'">E-mail</div>
              </v-card>
              <v-card outlined rounded="lg" class="flex-grow-1 text-center pa-3 source-card" :class="{ 'source-card--active': editedItem.notificationSource === 'teams' }" @click="editedItem.notificationSource = 'teams'">
                <v-icon size="24" :color="editedItem.notificationSource === 'teams' ? '#00E5FF' : 'grey'">mdi-microsoft-teams</v-icon>
                <div class="text-caption font-weight-bold mt-1" :class="editedItem.notificationSource === 'teams' ? 'text--primary' : 'text--secondary'">Teams</div>
              </v-card>
              <v-card outlined rounded="lg" class="flex-grow-1 text-center pa-3 source-card" :class="{ 'source-card--active': editedItem.notificationSource === 'both' }" @click="editedItem.notificationSource = 'both'">
                <div>
                  <v-icon size="20" :color="editedItem.notificationSource === 'both' ? '#00E5FF' : 'grey'" class="mr-1">mdi-email-outline</v-icon>
                  <v-icon size="20" :color="editedItem.notificationSource === 'both' ? '#00E5FF' : 'grey'">mdi-microsoft-teams</v-icon>
                </div>
                <div class="text-caption font-weight-bold mt-1" :class="editedItem.notificationSource === 'both' ? 'text--primary' : 'text--secondary'">Ambos</div>
              </v-card>
            </div>

            <v-divider class="theme-divider my-4" />

            <div class="text-subtitle-2 font-weight-bold text--primary mb-1">Público Alvo</div>
            <div class="text-caption text--secondary mb-3">Deixe vazio para incluir todos os colaboradores da empresa.</div>

            <v-row dense>
              <v-col cols="12" md="4">
                <v-autocomplete v-model="editedItem.targetDepartmentIds" :items="departments" item-text="name" item-value="id" label="Departamentos" prepend-inner-icon="mdi-domain" outlined dense multiple chips small-chips deletable-chips clearable :loading="loadingDepartments" class="elevment-input custom-chips" color="#00E5FF" hide-details="auto" />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete v-model="editedItem.targetJobRoleIds" :items="jobRoles" item-text="displayName" item-value="id" label="Cargos" prepend-inner-icon="mdi-briefcase" outlined dense multiple chips small-chips deletable-chips clearable :loading="loadingJobRoles" :disabled="loadingJobRoles" class="elevment-input custom-chips" color="#00E5FF" hide-details="auto" />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete v-model="editedItem.targetTeamIds" :items="activeTeams" item-text="name" item-value="id" label="Times" prepend-inner-icon="mdi-account-group" outlined dense multiple chips small-chips deletable-chips clearable :loading="loadingTeams" class="elevment-input custom-chips" color="#00E5FF" hide-details="auto" />
              </v-col>
            </v-row>
          </v-form>

          <v-alert v-if="errorMessage" type="error" outlined dense class="elevment-alert mt-4 text-body-2">
            {{ errorMessage }}
          </v-alert>
        </v-card-text>

        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn text height="40" class="px-5 font-weight-bold text--secondary" style="border-radius: 8px;" @click="closeDialog">Cancelar</v-btn>
          <v-btn :color="(validForm && !isViewOnlyMode) ? '#00E5FF' : 'grey'" height="40" depressed class="px-6 font-weight-bold" :class="(validForm && !isViewOnlyMode) ? 'black--text' : 'white--text'" style="border-radius: 8px;" :loading="saving" :disabled="!validForm || isViewOnlyMode" @click="saveCampaign">
            <v-icon left :color="(validForm && !isViewOnlyMode) ? 'black' : 'white'">mdi-content-save</v-icon>
            {{ editMode ? 'Atualizar' : 'Criar Campanha' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statsDialog" max-width="800px">
      <v-card v-if="selectedCampaign" class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex align-center">
          <v-icon color="#00E5FF" class="mr-2">mdi-chart-line</v-icon>
          <h3 class="text-h6 font-weight-bold text--primary">Estatísticas: {{ selectedCampaign.name }}</h3>
        </v-card-title>
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6">
          <v-row v-if="campaignStats" class="ma-n2">
            <v-col v-for="stat in statsCards" :key="stat.title" cols="12" sm="6" md="3" class="pa-2 d-flex">
              <v-card class="elevment-card elevation-0 flex-grow-1">
                <v-card-text class="pa-4 text-center d-flex flex-column justify-center align-center h-100">
                  <v-icon :color="stat.color" size="32" class="mb-2">{{ stat.icon }}</v-icon>
                  <div class="text-h5 font-weight-bold text--primary mb-1">{{ stat.value }}</div>
                  <div class="text-caption text--secondary">{{ stat.title }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div v-else class="pa-10 text-center">
            <v-progress-circular indeterminate color="#00E5FF" size="48" />
            <p class="mt-4 text--secondary">Carregando métricas...</p>
          </div>
        </v-card-text>
        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn color="#00E5FF" depressed height="40" class="px-6 font-weight-bold black--text" style="border-radius: 8px;" @click="statsDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmActivateDialog" max-width="500px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4">
          <h3 class="text-h5 font-weight-bold text--primary"><v-icon left color="success">mdi-rocket-launch</v-icon> Ativar Campanha</h3>
        </v-card-title>
        <v-card-text class="pa-6 pt-0">
          <p class="text-body-1 text--primary font-weight-medium mb-4">Deseja ativar e iniciar esta campanha agora?</p>
          
          <div class="elevment-box pa-4 mb-4">
            <div class="font-weight-bold text--primary mb-1">{{ campaignToActivate?.name }}</div>
            <div class="text-caption text--secondary d-flex align-center">
              <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
              Período: {{ formatDate(campaignToActivate?.startDate) }} a {{ formatDate(campaignToActivate?.endDate) }}
            </div>
          </div>
          
          <p class="text-body-2 text--secondary mb-0">
            Ao ativar, a campanha ficará disponível para todos os colaboradores definidos no público-alvo.
          </p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn text height="40" class="font-weight-bold text--secondary" style="border-radius: 8px;" @click="confirmActivateDialog = false" :disabled="activating">Cancelar</v-btn>
          <v-btn color="success" height="40" depressed class="px-6 font-weight-bold white--text" style="border-radius: 8px;" :loading="activating" @click="confirmActivateCampaign">
            <v-icon left color="white">mdi-rocket-launch</v-icon> Ativar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="800px">
      <v-card v-if="viewedCampaign" class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex align-center">
          <h3 class="text-h5 font-weight-bold text--primary"><v-icon left color="#00E5FF">mdi-eye-outline</v-icon> Detalhes da Campanha</h3>
        </v-card-title>
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6 custom-scroll" style="max-height: 70vh; overflow-y: auto;">
          <div class="mb-5">
            <div class="text-caption font-weight-bold text--secondary text-uppercase mb-1">Nome da Campanha</div>
            <div class="text-h6 text--primary">{{ viewedCampaign.name }}</div>
          </div>

          <div v-if="viewedCampaign.description" class="mb-5">
            <div class="text-caption font-weight-bold text--secondary text-uppercase mb-1">Descrição</div>
            <div class="text-body-2 text--primary" style="line-height: 1.5;">{{ viewedCampaign.description }}</div>
          </div>

          <v-row class="mb-4">
            <v-col cols="12" sm="4">
              <div class="text-caption font-weight-bold text--secondary text-uppercase mb-2">Status</div>
              <v-chip outlined small class="theme-badge" :class="getStatusChipClass(viewedCampaign.status)">
                {{ getStatusLabel(viewedCampaign.status) }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption font-weight-bold text--secondary text-uppercase mb-2">Data Início</div>
              <div class="text-body-2 font-weight-medium text--primary d-flex align-center">
                <v-icon x-small color="grey" class="mr-2">mdi-calendar</v-icon> {{ formatDate(viewedCampaign.startDate) }}
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption font-weight-bold text--secondary text-uppercase mb-2">Data Término</div>
              <div class="text-body-2 font-weight-medium text--primary d-flex align-center">
                <v-icon x-small color="grey" class="mr-2">mdi-flag-checkered</v-icon> {{ formatDate(viewedCampaign.endDate) }}
              </div>
            </v-col>
          </v-row>

          <v-alert v-if="viewedCampaign.status === 'CANCELLED' && viewedCampaign.cancellationReason" type="error" outlined dense class="elevment-alert mb-5 text-body-2">
            <div class="font-weight-bold mb-1">Motivo: {{ getCancellationReasonLabel(viewedCampaign.cancellationReason) }}</div>
            <div v-if="viewedCampaign.cancellationNotes">{{ viewedCampaign.cancellationNotes }}</div>
          </v-alert>

          <v-divider class="theme-divider my-4" />
          
          <div class="text-subtitle-2 font-weight-bold text--primary mb-3">Configuração de Público Alvo</div>

          <div v-if="viewedCampaign.targetDepartmentIds?.length > 0" class="mb-3">
            <div class="text-caption text--secondary mb-1"><v-icon x-small class="mr-1">mdi-domain</v-icon> Departamentos</div>
            <div class="d-flex flex-wrap gap-1">
              <v-chip v-for="dept in viewedCampaign.departments" :key="dept.id" x-small outlined class="theme-chip-topic">{{ dept.name }}</v-chip>
            </div>
          </div>

          <div v-if="viewedCampaign.targetJobRoleIds?.length > 0" class="mb-3">
            <div class="text-caption text--secondary mb-1"><v-icon x-small class="mr-1">mdi-briefcase</v-icon> Cargos</div>
            <div class="d-flex flex-wrap gap-1">
              <v-chip v-for="role in viewedCampaign.jobRoles" :key="role.id" x-small outlined class="theme-chip-topic">{{ role.title }}</v-chip>
            </div>
          </div>

          <div v-if="viewedCampaign.targetTeamIds?.length > 0" class="mb-3">
            <div class="text-caption text--secondary mb-1"><v-icon x-small class="mr-1">mdi-account-group</v-icon> Times</div>
            <div class="d-flex flex-wrap gap-1">
              <v-chip v-for="team in viewedCampaign.teams" :key="team.id" x-small outlined class="theme-chip-topic">{{ team.name }}</v-chip>
            </div>
          </div>

          <div v-if="(!viewedCampaign.targetDepartmentIds?.length) && (!viewedCampaign.targetJobRoleIds?.length) && (!viewedCampaign.targetTeamIds?.length)" class="elevment-box pa-3 text-center text-body-2 text--secondary">
            <v-icon small color="grey" class="mr-1">mdi-earth</v-icon> Campanha configurada para <strong>todos os colaboradores</strong>.
          </div>
        </v-card-text>
        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-btn v-if="canEdit && !isViewOnlyMode && viewedCampaign.status !== 'COMPLETED'" outlined color="#00E5FF" height="40" class="font-weight-bold action-btn-outlined" style="border-radius: 8px;" @click="editCampaignFromView">
            <v-icon left small>mdi-pencil</v-icon> Editar
          </v-btn>
          <v-spacer />
          <v-btn color="#00E5FF" depressed height="40" class="px-6 font-weight-bold black--text" style="border-radius: 8px;" @click="viewDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { CampaignsService, OrganizationService, TeamsService } from '@/services'

export default {
  name: 'CampaignsView',

  data: () => ({
    campaigns: [],
    loading: false,
    search: '',
    filterStatus: null,
    filterDate: null,
    filterEndDate: null,
    filterStartDateMenu: false,
    filterEndDateMenu: false,

    headers: [
      { text: 'Nome', value: 'name', sortable: true },
      { text: 'Status', value: 'status', sortable: true },
      { text: 'Início', value: 'startDate', sortable: true },
      { text: 'Término', value: 'endDate', sortable: true },
      { text: 'Público', value: 'target', sortable: false },
      { text: '', value: 'actions', sortable: false, align: 'end' },
    ],

    dialog: false,
    editMode: false,
    validForm: false,
    saving: false,
    errorMessage: '',
    startDateMenu: false,
    endDateMenu: false,
    
    editedItem: {
      name: '', description: '', startDate: null, endDate: null, status: 'DRAFT', notificationSource: 'both',
      emailEnabled: false, teamsEnabled: false, cancellationReason: null, cancellationNotes: null,
      targetDepartmentIds: [], targetJobRoleIds: [], targetTeamIds: [],
    },
    
    defaultItem: {
      name: '', description: '', startDate: null, endDate: null, status: 'DRAFT', notificationSource: 'both',
      emailEnabled: false, teamsEnabled: false, cancellationReason: null, cancellationNotes: null,
      targetDepartmentIds: [], targetJobRoleIds: [], targetTeamIds: [],
    },

    statusOptions: [
      { text: 'Rascunho', value: 'DRAFT' },
      { text: 'Ativa', value: 'ACTIVE' },
      { text: 'Concluída', value: 'COMPLETED' },
      { text: 'Cancelada', value: 'CANCELLED' },
    ],

    cancellationReasons: [
      { text: 'Falta de participação', value: 'BAIXA_PARTICIPACAO' },
      { text: 'Mudança na estratégia', value: 'MUDANCA_ESTRATEGICA' },
      { text: 'Reestruturação', value: 'REESTRUTURACAO' },
      { text: 'Problemas técnicos', value: 'PROBLEMAS_TECNICOS' },
      { text: 'Período inadequado', value: 'PERIODO_INADEQUADO' },
      { text: 'Outro motivo', value: 'OUTRO' },
    ],

    departments: [],
    jobRoles: [],
    allJobRoles: [],
    teams: [],
    loadingDepartments: false,
    loadingJobRoles: false,
    loadingTeams: false,

    statsDialog: false,
    selectedCampaign: null,
    campaignStats: null,
    statsCards: [],

    viewDialog: false,
    viewedCampaign: null,

    confirmActivateDialog: false,
    campaignToActivate: null,
    activating: false,

    rules: {
      required: v => !!v || 'Campo obrigatório',
    },
    searchTimeout: null,
  }),

  computed: {
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}')
      } catch { return {} }
    },
    isUser() {
      return this.currentUser.role === 'USER'
    },
    isViewOnlyMode() {
      try { return this.$store?.state?.viewOnlyMode === true } catch { return false }
    },
    canEdit() {
      return !this.isUser
    },
    statusOptionsFiltered() {
      if (this.isUser) {
        return this.statusOptions.filter(opt => ['ACTIVE', 'COMPLETED'].includes(opt.value))
      }
      return this.statusOptions
    },
    today() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    filteredCampaigns() {
      let filtered = [...this.campaigns]

      if (this.isUser) {
        filtered = filtered.filter(c => c.status === 'ACTIVE' || c.status === 'COMPLETED')
      }
      if (this.search && this.search.trim()) {
        const s = this.search.trim().toLowerCase()
        filtered = filtered.filter(c => c.name.toLowerCase().includes(s))
      }
      if (this.filterStatus) {
        filtered = filtered.filter(c => c.status === this.filterStatus)
      }
      if (this.filterDate && this.filterEndDate) {
        filtered = filtered.filter(c => c.startDate >= this.filterDate && c.endDate <= this.filterEndDate)
      } else if (this.filterDate) {
        filtered = filtered.filter(c => c.startDate >= this.filterDate)
      } else if (this.filterEndDate) {
        filtered = filtered.filter(c => c.endDate <= this.filterEndDate)
      }
      return filtered
    },
    activeTeams() {
      return this.teams.filter(t => t.status !== 'ARCHIVED')
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.loadDepartments()
        this.loadAllJobRoles()
        this.loadTeams()
      }
    },
    'editedItem.targetDepartmentIds': {
      handler(newIds) { this.filterJobRolesByDepartments(newIds) },
      deep: true,
    },
    'editedItem.status': {
      handler(newStatus) {
        if (newStatus !== 'CANCELLED') {
          this.editedItem.cancellationReason = null
          this.editedItem.cancellationNotes = null
        }
      },
    },
  },

  mounted() {
    this.loadCampaigns()
  },

  methods: {
    async loadCampaigns() {
      this.loading = true
      try {
        const response = await CampaignsService.list()
        let camps = response.data.content || response.data || []
        if (this.isUser) camps = camps.filter(c => c.status !== 'DRAFT')
        this.campaigns = camps
      } catch (error) {
        if(this.$notify) this.$notify({ type: 'error', text: 'Erro ao carregar campanhas' })
      } finally {
        this.loading = false
      }
    },
    async loadDepartments() {
      this.loadingDepartments = true
      try {
        const response = await OrganizationService.departments.list()
        const all = response.data.content || response.data || []
        this.departments = all.filter(d => d.active === true)
      } catch (error) {
        // block error
      } finally { this.loadingDepartments = false }
    },
    async loadTeams() {
      this.loadingTeams = true
      try {
        const response = await TeamsService.list()
        this.teams = response.data || []
      } catch (error) {
        // block error
      } finally { this.loadingTeams = false }
    },
    async loadAllJobRoles() {
      this.loadingJobRoles = true
      this.allJobRoles = []
      this.jobRoles = []
      try {
        if (this.departments.length === 0) await this.loadDepartments()
        const promises = this.departments.map(dept => 
          OrganizationService.jobRoles.getByDepartment(dept.id).then(res => ({
            deptId: dept.id, deptName: dept.name, roles: res.data || []
          })).catch(() => ({ deptId: dept.id, deptName: dept.name, roles: [] }))
        )
        const results = await Promise.all(promises)
        results.forEach(r => {
          r.roles.forEach(role => {
            this.allJobRoles.push({ ...role, departmentId: r.deptId, displayName: `${role.title} (${r.deptName})` })
          })
        })
        this.jobRoles = [...this.allJobRoles]
      } catch (error) {
        // block error
      } finally { this.loadingJobRoles = false }
    },
    filterJobRolesByDepartments(deptIds) {
      if (!deptIds || deptIds.length === 0) {
        this.jobRoles = [...this.allJobRoles]
      } else {
        this.jobRoles = this.allJobRoles.filter(jr => deptIds.includes(jr.departmentId))
        this.editedItem.targetJobRoleIds = this.editedItem.targetJobRoleIds.filter(id => this.jobRoles.some(jr => jr.id === id))
      }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {}, 300)
    },
    clearFilters() {
      this.search = ''
      this.filterStatus = null
      this.filterDate = null
      this.filterEndDate = null
    },
    openCreateDialog() {
      this.editMode = false
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedItem.notificationSource = 'both'
      this.errorMessage = ''
      this.dialog = true
    },
    editCampaign(campaign) {
      this.editMode = true
      let ns = 'both'
      if (campaign.emailEnabled && campaign.teamsEnabled) ns = 'both'
      else if (campaign.emailEnabled) ns = 'email'
      else if (campaign.teamsEnabled) ns = 'teams'

      this.editedItem = {
        ...campaign, notificationSource: ns, emailEnabled: !!campaign.emailEnabled, teamsEnabled: !!campaign.teamsEnabled,
        targetDepartmentIds: campaign.targetDepartmentIds || [], targetJobRoleIds: campaign.targetJobRoleIds || [], targetTeamIds: campaign.targetTeamIds || []
      }
      this.errorMessage = ''
      this.dialog = true
    },
    viewCampaign(campaign) {
      this.viewedCampaign = {
        ...campaign, targetDepartmentIds: campaign.targetDepartmentIds || [], targetJobRoleIds: campaign.targetJobRoleIds || [], targetTeamIds: campaign.targetTeamIds || []
      }
      this.viewDialog = true
    },
    editCampaignFromView() {
      const camp = this.viewedCampaign
      this.viewDialog = false
      this.$nextTick(() => this.editCampaign(camp))
    },
    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editMode = false
        this.errorMessage = ''
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    async consolidateTargetFromTeams() {
      const tIds = this.editedItem.targetTeamIds || []
      if (tIds.length === 0) return { departmentIds: this.editedItem.targetDepartmentIds || [], jobRoleIds: this.editedItem.targetJobRoleIds || [] }
      try {
        const promises = tIds.map(tId => TeamsService.getMembers(tId))
        const resps = await Promise.all(promises)
        const members = resps.flatMap(r => r.data || [])
        const tDepts = [...new Set(members.filter(m => m.department?.id).map(m => m.department.id))]
        const tRoles = [...new Set(members.filter(m => m.jobRole?.id).map(m => m.jobRole.id))]
        return {
          departmentIds: [...new Set([...(this.editedItem.targetDepartmentIds || []), ...tDepts])],
          jobRoleIds: [...new Set([...(this.editedItem.targetJobRoleIds || []), ...tRoles])],
        }
      } catch (error) {
        return { departmentIds: this.editedItem.targetDepartmentIds || [], jobRoleIds: this.editedItem.targetJobRoleIds || [] }
      }
    },
    async saveCampaign() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      this.errorMessage = ''
      try {
        const consolidated = await this.consolidateTargetFromTeams()
        const payload = {
          ...this.editedItem, targetDepartmentIds: consolidated.departmentIds, targetJobRoleIds: consolidated.jobRoleIds, targetTeamIds: this.editedItem.targetTeamIds || []
        }
        const ns = (this.editedItem.notificationSource || '').toLowerCase()
        if (ns === 'both') { payload.emailEnabled = true; payload.teamsEnabled = true }
        else if (ns === 'email') { payload.emailEnabled = true; payload.teamsEnabled = false }
        else if (ns === 'teams') { payload.emailEnabled = false; payload.teamsEnabled = true }
        else { payload.emailEnabled = !!this.editedItem.emailEnabled; payload.teamsEnabled = !!this.editedItem.teamsEnabled }

        if (this.editMode) await CampaignsService.update(this.editedItem.id, payload)
        else await CampaignsService.create(payload)

        await this.loadCampaigns()
        this.closeDialog()
        if (this.$notify) this.$notify({ type: 'success', text: this.editMode ? 'Campanha atualizada!' : 'Campanha criada!' })
      } catch (error) {
        if (error.response && error.response.status >= 200 && error.response.status < 300) {
          await this.loadCampaigns()
          this.closeDialog()
          return
        }
        this.errorMessage = error.response?.data?.message || 'Erro ao salvar campanha'
        if (this.$notify) this.$notify({ type: 'error', text: this.errorMessage })
      } finally { this.saving = false }
    },
    launchCampaign(campaign) {
      this.campaignToActivate = campaign
      this.confirmActivateDialog = true
    },
    async confirmActivateCampaign() {
      if (!this.campaignToActivate) return
      this.activating = true
      try {
        await CampaignsService.update(this.campaignToActivate.id, { ...this.campaignToActivate, status: 'ACTIVE' })
        await this.loadCampaigns()
        this.confirmActivateDialog = false
        this.campaignToActivate = null
        if (this.$notify) this.$notify({ type: 'success', text: 'Campanha ativada!' })
      } catch (error) {
        if (this.$notify) this.$notify({ type: 'error', text: 'Erro ao ativar campanha' })
      } finally { this.activating = false }
    },
    async viewStats(campaign) {
      this.selectedCampaign = campaign
      this.statsDialog = true
      this.campaignStats = null
      try {
        const response = await CampaignsService.getStats(campaign.id)
        this.campaignStats = response.data
        this.statsCards = [
          { title: 'Participantes', value: this.campaignStats.totalParticipants || 0, icon: 'mdi-account-group', color: '#00E5FF' },
          { title: 'Concluídas', value: this.campaignStats.completedEvaluations || 0, icon: 'mdi-check-circle', color: 'success' },
          { title: 'Pendentes', value: this.campaignStats.pendingEvaluations || 0, icon: 'mdi-clock-alert', color: 'warning' },
          { title: 'Taxa', value: `${this.campaignStats.completionRate || 0}%`, icon: 'mdi-poll', color: 'purple' },
        ]
      } catch (error) {
        if(this.$notify) this.$notify({ type: 'error', text: 'Erro ao carregar estatísticas' })
        this.statsDialog = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [y, m, d] = dateString.split('-')
        return new Date(y, m - 1, d).toLocaleDateString('pt-BR')
      }
      return new Date(dateString).toLocaleDateString('pt-BR')
    },
    getStatusLabel(status) {
      const labels = { DRAFT: 'Rascunho', ACTIVE: 'Ativa', COMPLETED: 'Concluída', CANCELLED: 'Cancelada' }
      return labels[status] || status
    },
    getStatusChipClass(status) {
      const classes = { DRAFT: 'grey--text', ACTIVE: 'success--text', COMPLETED: 'cyan--text', CANCELLED: 'error--text' }
      return classes[status] || 'grey--text'
    },
    getCancellationReasonLabel(reason) {
      const obj = this.cancellationReasons.find(r => r.value === reason)
      return obj ? obj.text : reason
    },
    getTargetSummary(campaign) {
      const total = (campaign.targetDepartmentIds?.length || 0) + (campaign.targetJobRoleIds?.length || 0) + (campaign.targetTeamIds?.length || 0)
      return total === 0 ? 'Todos' : `${total} filtros aplicados`
    },
    async sendEmail(campaign) {
      if (!confirm(`Deseja enviar e-mails para a campanha "${campaign.name}"?`)) return
      try {
        const resp = await CampaignsService.notifyEmail(campaign.id)
        if (this.$notify) this.$notify({ type: 'success', text: resp.data?.message || 'E-mails enviados' })
      } catch (error) {
        if (this.$notify) this.$notify({ type: 'error', text: 'Erro ao disparar e-mails' })
      }
    },
    async sendTeams(campaign) {
      if (!confirm(`Deseja enviar notificações Teams para "${campaign.name}"?`)) return
      try {
        const resp = await CampaignsService.notifyTeams(campaign.id)
        if (this.$notify) this.$notify({ type: 'success', text: resp.data?.message || 'Teams enviado' })
      } catch (error) {
        if (this.$notify) this.$notify({ type: 'error', text: 'Erro ao disparar Teams' })
      }
    },
    async duplicateCampaign(campaign) {
      try {
        const resp = await CampaignsService.duplicate(campaign.id)
        this.editMode = true
        this.editedItem = { ...resp.data, targetDepartmentIds: resp.data.targetDepartmentIds || [], targetJobRoleIds: resp.data.targetJobRoleIds || [] }
        this.dialog = true
      } catch (error) {
        this.editMode = false
        this.editedItem = { ...this.defaultItem, name: campaign.name + ' (cópia)', description: campaign.description, targetDepartmentIds: campaign.targetDepartmentIds ? [...campaign.targetDepartmentIds] : [] }
        this.dialog = true
      }
    },
    async deleteCampaign(campaign) {
      if (!confirm(`Deseja excluir a campanha "${campaign.name}"?`)) return
      try {
        await CampaignsService.delete(campaign.id)
        await this.loadCampaigns()
        if (this.$notify) this.$notify({ type: 'success', text: 'Campanha excluída' })
      } catch (error) {
        if (this.$notify) this.$notify({ type: 'error', text: 'Erro ao excluir' })
      }
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  max-width: 1440px;
  margin: 0 auto;
}

/* Base Cards */
.elevment-card {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  background-color: var(--bg-card, #1A1A1A) !important;
}

/* Componentes Base - Inputs, Dialogs e Divider */
.theme-divider {
  border-color: var(--border-color, rgba(128,128,128,0.15)) !important;
}

.elevment-dialog-card {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  background-color: var(--bg-card, #1A1A1A) !important;
}

.elevment-box {
  background-color: var(--bg-secondary, rgba(128,128,128,0.05));
  border-radius: 8px;
  border: 1px solid var(--border-color, rgba(128,128,128,0.1));
}

.elevment-alert {
  border-radius: 8px !important;
  background-color: transparent !important;
}

/* Tabela e Links */
::v-deep .custom-table {
  background: transparent !important;
}
::v-deep .custom-table th {
  border-bottom: 1px solid var(--border-color, rgba(128,128,128,0.15)) !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.05em;
  color: var(--text-secondary, rgba(128,128,128,0.8)) !important;
}
::v-deep .custom-table td {
  border-bottom: 1px solid var(--border-color, rgba(128,128,128,0.08)) !important;
}

.campaign-name-link {
  color: var(--text-primary, #FFFFFF);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
}
.campaign-name-link:hover {
  color: #00E5FF;
}

/* Tags (Chips/Badges) */
::v-deep .theme-badge {
  border-radius: 9999px !important;
  background: transparent !important;
  border: 1px solid currentColor !important;
  text-transform: uppercase;
  font-weight: 700 !important;
  font-size: 10px !important;
}
::v-deep .theme-chip-topic {
  border-radius: 9999px !important;
  background: transparent !important;
  border: 1px solid var(--border-color, rgba(128,128,128,0.2)) !important;
  color: var(--text-secondary, #AEAEB2) !important;
}

::v-deep .custom-chips .v-chip {
  border-radius: 6px !important;
}

/* Interações */
.action-btn { letter-spacing: 0 !important; }
.action-btn-outlined { border-color: rgba(128, 128, 128, 0.3) !important; letter-spacing: 0 !important; }

.source-card {
  border: 1px solid var(--border-color, rgba(128,128,128,0.2)) !important;
  background: transparent !important;
  cursor: pointer;
  transition: all 0.2s ease;
}
.source-card:hover {
  border-color: rgba(0, 229, 255, 0.5) !important;
}
.source-card--active {
  border-color: #00E5FF !important;
  background-color: rgba(0, 229, 255, 0.05) !important;
}

/* Scroll Customizado */
.custom-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scroll::-webkit-scrollbar-thumb { background: rgba(128, 128, 128, 0.3); border-radius: 3px; }
.custom-scroll::-webkit-scrollbar-track { background: transparent; }
</style>